// ============================================================
// JavaQuiz.js
//
// Gamified Java Mastery Quiz — ProDev Platform
// Converted from TypeScript to plain JavaScript (.js / .jsx)
//
// Features:
//   - Personalised welcome screen (greets user by name)
//   - Rules & conduct screen with time limits explained
//   - Web Speech API — audible "Ready? Begin!" on quiz start
//   - Dynamic difficulty: re-arranges easy → hard on retry
//     based on initial performance per category
//   - Star reward system:
//       ★★★ = 100%  → Golden Buzzer + Certification Exam unlocked
//       ★★  = 90%+  → Certification Exam unlocked
//       ★   = 80%+  → Certification Exam unlocked
//       ✗   = <80%  → must retake (dynamically reordered)
//   - Anti-cheat engine:
//       • Tab/window visibility detection
//       • Browser focus loss detection
//       • Right-click disabled during quiz
//       • Copy/paste keyboard shortcuts blocked
//       • DevTools size-change detection
//       • Violations logged and shown on results screen
//   - Sound synthesis (Web Audio API — no external audio files):
//       • Correct answer chime
//       • Wrong answer buzz
//       • Timer warning beep (last 60 seconds)
//       • Quiz complete fanfare
//       • Golden buzzer sound (100% score)
//   - Category-level performance breakdown
//   - Leaderboard (localStorage)
//   - Brand colours: #100720 dark, #E5C95F gold
//
// Junior dev notes are scattered throughout explaining every
// major concept, hook, and pattern used in this file.
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Button, Card, CardContent, Typography,
    LinearProgress, Chip, Divider,
    styled, keyframes,
} from '@mui/material';
import { Link } from 'react-router-dom';

// ── MUI Icons ─────────────────────────────────────────────────
import StarIcon               from '@mui/icons-material/Star';
import StarBorderIcon         from '@mui/icons-material/StarBorder';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon       from '@mui/icons-material/WarningAmber';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import TimerIcon              from '@mui/icons-material/Timer';
import SchoolIcon             from '@mui/icons-material/School';
import SecurityIcon           from '@mui/icons-material/Security';
import PlayArrowIcon          from '@mui/icons-material/PlayArrow';
import ReplayIcon             from '@mui/icons-material/Replay';
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BusinessCenterIcon     from '@mui/icons-material/BusinessCenter';

// ============================================================
//  BRAND CONSTANTS
//  Single source of truth — same palette as Header, Vision, Mission.
// ============================================================
const BRAND = {
    dark:       '#100720',
    darkCard:   '#1a0f35',
    darkBorder: 'rgba(229,201,95,0.2)',
    gold:       '#E5C95F',
    goldenrod:  '#DAA520',
    goldFaint:  'rgba(229,201,95,0.08)',
    white:      '#FFFFFF',
    dimWhite:   'rgba(255,255,255,0.65)',
    success:    '#3ECF8E',
    error:      '#FF6B6B',
    warning:    '#FF9F43',
};

// ============================================================
//  QUIZ CONFIGURATION
//  Change these values to adjust quiz behaviour globally.
//  No need to hunt through the component — all config is here.
// ============================================================
const QUIZ_CONFIG = {
    totalTimeSeconds: 1200,   // 20 minutes for full quiz
    warningThreshold: 60,     // Seconds left when timer turns red + beeps
    passMark:         0.80,   // 80% minimum to earn at least 1 star
    twoStarMark:      0.90,   // 90% for 2 stars
    threeStarMark:    1.00,   // 100% for 3 stars (Golden Buzzer!)
    maxViolations:    3,      // Violations before quiz is auto-terminated
    leaderboardKey:   'java_quiz_leaderboard',
};

// ============================================================
//  QUESTION BANK
//  All quiz questions with category and difficulty tags.
//  difficulty drives adaptive reordering on retry.
// ============================================================
const javaQuestionBank = [
    // ── Datatypes ─────────────────────────────────────────────
    { question: 'Which datatype is used to store a single character in Java?', options: ['String', 'char', 'Character', 'byte'], answer: 'char', explanation: '`char` stores a single 16-bit Unicode character.', link: '/java-datatypes', category: 'Datatypes', difficulty: 'easy' },
    { question: 'What is the default value of a boolean in Java?', options: ['true', 'false', 'null', '0'], answer: 'false', explanation: 'Booleans default to `false` in Java.', link: '/java-datatypes', category: 'Datatypes', difficulty: 'easy' },
    { question: 'Which datatype is used for decimal numbers?', options: ['int', 'float', 'char', 'boolean'], answer: 'float', explanation: '`float` and `double` are used for decimal values.', link: '/java-datatypes', category: 'Datatypes', difficulty: 'easy' },
    // ── Variables ─────────────────────────────────────────────
    { question: 'Which keyword is used to declare a constant variable?', options: ['const', 'final', 'static', 'immutable'], answer: 'final', explanation: '`final` prevents reassignment of a variable.', link: '/java-variables', category: 'Variables', difficulty: 'easy' },
    { question: 'Which of the following is a valid variable name?', options: ['2value', 'value$', 'class', 'void'], answer: 'value$', explanation: 'Variable names cannot start with digits or use reserved keywords.', link: '/java-variables', category: 'Variables', difficulty: 'easy' },
    { question: 'What is the scope of a local variable?', options: ['Global', 'Class-wide', 'Method-wide', 'Block-level'], answer: 'Block-level', explanation: 'Local variables are accessible only within the block they are defined.', link: '/java-variables', category: 'Variables', difficulty: 'medium' },
    // ── Control Structures ────────────────────────────────────
    { question: 'Which loop is guaranteed to execute at least once?', options: ['for', 'while', 'do-while', 'foreach'], answer: 'do-while', explanation: '`do-while` executes the loop body before checking the condition.', link: '/java-control-structures', category: 'Control Structures', difficulty: 'easy' },
    { question: 'Which keyword is used to exit a loop prematurely?', options: ['continue', 'exit', 'break', 'return'], answer: 'break', explanation: '`break` exits the loop immediately.', link: '/java-control-structures', category: 'Control Structures', difficulty: 'easy' },
    { question: 'What does the `continue` statement do?', options: ['Exits the loop', 'Skips the current iteration', 'Ends the method', 'Throws an exception'], answer: 'Skips the current iteration', explanation: '`continue` skips to the next iteration of the loop.', link: '/java-control-structures', category: 'Control Structures', difficulty: 'medium' },
    // ── Methods ───────────────────────────────────────────────
    { question: 'Which keyword is used to define a method?', options: ['function', 'method', 'def', 'void'], answer: 'void', explanation: '`void` is used when a method does not return a value.', link: '/java-methods', category: 'Methods', difficulty: 'easy' },
    { question: 'Which part of a method defines its return type?', options: ['Parameters', 'Body', 'Signature', 'Declaration'], answer: 'Declaration', explanation: 'The method declaration includes the return type.', link: '/java-methods', category: 'Methods', difficulty: 'medium' },
    { question: 'What is method overloading?', options: ['Using multiple classes', 'Defining methods with same name but different parameters', 'Calling a method recursively', 'Using inheritance'], answer: 'Defining methods with same name but different parameters', explanation: 'Overloading allows multiple methods with the same name but different signatures.', link: '/java-methods', category: 'Methods', difficulty: 'medium' },
    // ── Scopes ────────────────────────────────────────────────
    { question: 'Which scope applies to static variables?', options: ['Instance', 'Method', 'Class', 'Block'], answer: 'Class', explanation: 'Static variables belong to the class, not instances.', link: '/java-scopes', category: 'Scopes', difficulty: 'medium' },
    { question: 'Which scope does a parameter variable have?', options: ['Global', 'Method', 'Class', 'Package'], answer: 'Method', explanation: 'Parameters are scoped to the method they are declared in.', link: '/java-scopes', category: 'Scopes', difficulty: 'easy' },
    { question: 'What is the scope of a public field?', options: ['Package', 'Class', 'Global', 'Accessible from anywhere'], answer: 'Accessible from anywhere', explanation: '`public` fields can be accessed from any class.', link: '/java-scopes', category: 'Scopes', difficulty: 'medium' },
    // ── OOP ───────────────────────────────────────────────────
    { question: 'Which keyword is used to inherit a class?', options: ['implements', 'extends', 'inherits', 'super'], answer: 'extends', explanation: '`extends` is used for class inheritance.', link: '/java-oop', category: 'OOP', difficulty: 'easy' },
    { question: 'What is encapsulation?', options: ['Hiding internal state and requiring access through methods', 'Using multiple classes', 'Overloading methods', 'Inheriting from multiple classes'], answer: 'Hiding internal state and requiring access through methods', explanation: 'Encapsulation protects object state using access modifiers.', link: '/java-oop', category: 'OOP', difficulty: 'medium' },
    { question: 'Which keyword refers to the current object?', options: ['this', 'super', 'self', 'object'], answer: 'this', explanation: '`this` refers to the current instance of the class.', link: '/java-oop', category: 'OOP', difficulty: 'easy' },
    // ── Arrays and Strings ────────────────────────────────────
    { question: 'Which method returns the length of a string in Java?', options: ['size()', 'length()', 'getLength()', 'count()'], answer: 'length()', explanation: '`length()` returns the number of characters in a string.', link: '/java-strings', category: 'Arrays and Strings', difficulty: 'easy' },
    { question: 'How do you declare an array of integers?', options: ['int[] arr;', 'int arr[];', 'int arr();', 'array<int> arr;'], answer: 'int[] arr;', explanation: 'Java uses `int[]` syntax to declare arrays.', link: '/java-arrays', category: 'Arrays and Strings', difficulty: 'easy' },
    { question: 'Which method is used to compare two strings?', options: ['==', 'equals()', 'compare()', 'match()'], answer: 'equals()', explanation: '`equals()` compares string contents, not references.', link: '/java-strings', category: 'Arrays and Strings', difficulty: 'medium' },
    // ── Exception Handling ────────────────────────────────────
    { question: 'Which keyword is used to handle exceptions?', options: ['try', 'catch', 'throw', 'All of the above'], answer: 'All of the above', explanation: '`try`, `catch`, and `throw` are all part of exception handling.', link: '/java-exceptions', category: 'Exception Handling', difficulty: 'easy' },
    { question: 'Which class is the superclass of all exceptions?', options: ['Throwable', 'Error', 'Exception', 'RuntimeException'], answer: 'Throwable', explanation: '`Throwable` is the root class for all errors and exceptions.', link: '/java-exceptions', category: 'Exception Handling', difficulty: 'hard' },
    { question: 'What does the `finally` block do?', options: ['Handles exceptions', 'Executes only if an exception occurs', 'Executes regardless of exception', 'Terminates the program'], answer: 'Executes regardless of exception', explanation: '`finally` always runs after `try` and `catch`.', link: '/java-exceptions', category: 'Exception Handling', difficulty: 'medium' },
    // ── File Handling ─────────────────────────────────────────
    { question: 'Which class is used to read text files?', options: ['FileReader', 'Scanner', 'BufferedReader', 'All of the above'], answer: 'All of the above', explanation: 'Each class can read files in different ways.', link: '/java-file-handling', category: 'File Handling', difficulty: 'medium' },
    { question: 'Which package contains file handling classes?', options: ['java.io', 'java.util', 'java.file', 'java.fs'], answer: 'java.io', explanation: '`java.io` provides classes for file input/output.', link: '/java-file-handling', category: 'File Handling', difficulty: 'easy' },
    { question: 'Which class is used to write to a file?', options: ['FileWriter', 'PrintWriter', 'BufferedWriter', 'All of the above'], answer: 'All of the above', explanation: 'Each class offers different ways to write to files.', link: '/java-file-handling', category: 'File Handling', difficulty: 'medium' },
    // ── Collections ───────────────────────────────────────────
    { question: 'Which interface does ArrayList implement?', options: ['List', 'Set', 'Map', 'Queue'], answer: 'List', explanation: '`ArrayList` implements the `List` interface.', link: '/java-collections', category: 'Collections', difficulty: 'easy' },
    { question: 'Which collection does not allow duplicate elements?', options: ['List', 'Set', 'Map', 'Queue'], answer: 'Set', explanation: '`Set` ensures all elements are unique.', link: '/java-collections', category: 'Collections', difficulty: 'medium' },
    { question: 'Which class stores key-value pairs?', options: ['List', 'Set', 'Map', 'Queue'], answer: 'Map', explanation: '`Map` stores data as key-value pairs.', link: '/java-collections', category: 'Collections', difficulty: 'easy' },
    // ── Interfaces and Abstraction ────────────────────────────
    { question: 'Which keyword is used to implement an interface?', options: ['extends', 'implements', 'inherits', 'interface'], answer: 'implements', explanation: '`implements` is used to define interface behavior in a class.', link: '/java-interfaces', category: 'Interfaces and Abstraction', difficulty: 'easy' },
    { question: 'Can an interface contain method implementations?', options: ['No', 'Yes, using default methods', 'Only static methods', 'Only abstract methods'], answer: 'Yes, using default methods', explanation: 'Java 8 introduced default methods in interfaces.', link: '/java-interfaces', category: 'Interfaces and Abstraction', difficulty: 'hard' },
    { question: 'What is the purpose of an abstract class?', options: ['To create objects', 'To define common behavior without full implementation', 'To inherit multiple classes', 'To override static methods'], answer: 'To define common behavior without full implementation', explanation: 'Abstract classes provide partial implementation for subclasses.', link: '/java-abstraction', category: 'Interfaces and Abstraction', difficulty: 'hard' },
    // ── Lambda Expressions ────────────────────────────────────
    { question: 'Which version of Java introduced lambda expressions?', options: ['Java 6', 'Java 7', 'Java 8', 'Java 11'], answer: 'Java 8', explanation: 'Lambda expressions were introduced in Java 8.', link: '/java-lambda', category: 'Lambda Expressions', difficulty: 'medium' },
    { question: 'What is the syntax of a lambda expression?', options: ['(args) -> expression', 'args => expression', 'function(args)', 'lambda(args)'], answer: '(args) -> expression', explanation: 'Java uses `(args) -> expression` syntax for lambdas.', link: '/java-lambda', category: 'Lambda Expressions', difficulty: 'medium' },
    { question: 'Which interface type is required for lambda expressions?', options: ['Functional interface', 'Abstract class', 'Serializable', 'Runnable'], answer: 'Functional interface', explanation: 'Lambdas require interfaces with a single abstract method.', link: '/java-lambda', category: 'Lambda Expressions', difficulty: 'hard' },
    // ── Multithreading ────────────────────────────────────────
    { question: 'Which class is used to create threads?', options: ['Thread', 'Runnable', 'Executor', 'Process'], answer: 'Thread', explanation: '`Thread` is the base class for multithreading.', link: '/java-multithreading', category: 'Multithreading', difficulty: 'medium' },
    { question: 'Which method starts a thread?', options: ['run()', 'start()', 'execute()', 'launch()'], answer: 'start()', explanation: '`start()` begins thread execution by calling `run()` internally.', link: '/java-multithreading', category: 'Multithreading', difficulty: 'medium' },
    { question: 'Which keyword ensures thread safety?', options: ['volatile', 'synchronized', 'static', 'final'], answer: 'synchronized', explanation: '`synchronized` prevents concurrent access to critical sections.', link: '/java-multithreading', category: 'Multithreading', difficulty: 'hard' },
];

// ============================================================
//  UTILITY FUNCTIONS
//  Pure functions — no side effects, easy to test independently.
// ============================================================

/**
 * shuffle
 * Returns a new randomly ordered copy of the input array.
 * Does NOT mutate the original — uses spread [...arr] first.
 */
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

/**
 * getStars
 * Returns 0–3 based on score percentage.
 * 0 = failed, 1 = 80%+, 2 = 90%+, 3 = 100%
 */
const getStars = (score, total) => {
    const ratio = score / total;
    if (ratio >= QUIZ_CONFIG.threeStarMark) return 3;
    if (ratio >= QUIZ_CONFIG.twoStarMark)   return 2;
    if (ratio >= QUIZ_CONFIG.passMark)       return 1;
    return 0;
};

/**
 * getBadge
 * Returns a badge label string based on star count.
 */
const getBadge = (stars) => {
    if (stars === 3) return '🧙 Java Wizard';
    if (stars === 2) return '⚡ Java Pro';
    if (stars === 1) return '📘 Java Apprentice';
    return '🌱 Java Novice';
};

/**
 * formatTime
 * Converts raw seconds to MM:SS display string.
 * padStart(2, '0') ensures single digits show as "05" not "5".
 */
const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

/**
 * saveScore / loadScores
 * Persist the top 5 results to localStorage.
 * localStorage only accepts strings, so we JSON.stringify/parse.
 */
const saveScore = (entry) => {
    const prev = JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');
    const updated = [entry, ...prev].slice(0, 5);
    localStorage.setItem(QUIZ_CONFIG.leaderboardKey, JSON.stringify(updated));
};

const loadScores = () =>
    JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');

/**
 * reorderByDifficulty
 * On retry, sorts questions so that categories the user struggled
 * with (< 50% correct) appear LAST, and strong categories FIRST.
 * This is the adaptive difficulty mechanism.
 *
 * Junior dev note: We sort by the user's accuracy ratio per category.
 * Higher ratio = user is good at it = show it first (easy warmup).
 * Lower ratio = user struggled = show it last (harder section).
 */
const reorderByDifficulty = (questions, categoryScores) => {
    return [...questions].sort((a, b) => {
        const aRatio = categoryScores[a.category]
            ? categoryScores[a.category].correct / categoryScores[a.category].total
            : 0.5;
        const bRatio = categoryScores[b.category]
            ? categoryScores[b.category].correct / categoryScores[b.category].total
            : 0.5;
        return bRatio - aRatio; // descending — best first
    });
};

// ============================================================
//  SOUND ENGINE — Web Audio API
//
//  All sounds are synthesised in the browser — no audio files.
//  Web Audio API is supported in all modern browsers.
//
//  Junior dev note: AudioContext must be created AFTER a user
//  gesture (e.g. a click) due to browser autoplay policies.
//  We use lazy initialisation — audioCtx is null until first use.
// ============================================================

let audioCtx = null;

/**
 * getAudioContext
 * Returns the shared AudioContext, creating it lazily on first call.
 * webkitAudioContext is the Safari fallback (older versions).
 * We use window['webkitAudioContext'] (bracket notation) instead of
 * `window as any` (TypeScript) since this is plain JavaScript.
 */
const getAudioContext = () => {
    if (!audioCtx) {
        const AudioContextClass = window.AudioContext || window['webkitAudioContext'];
        audioCtx = new AudioContextClass();
    }
    return audioCtx;
};

/**
 * playTone
 * Core sound primitive. Creates an oscillator at a given frequency,
 * connects it through a gain node for volume control, and plays it.
 *
 * frequency — Hz pitch (440 = A4, 523 = C5, etc.)
 * duration  — seconds the tone lasts
 * type      — wave shape: 'sine' | 'square' | 'sawtooth' | 'triangle'
 * volume    — 0.0 to 1.0
 * delay     — seconds before tone starts (enables chord sequences)
 */
const playTone = (frequency, duration, type = 'sine', volume = 0.3, delay = 0) => {
    try {
        const ctx  = getAudioContext();
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = type;
        osc.frequency.setValueAtTime(frequency, ctx.currentTime + delay);

        // Fade in then fade out — prevents harsh audio clicks at start/end
        gain.gain.setValueAtTime(0, ctx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + delay + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + delay + duration);

        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + duration + 0.05);
    } catch (e) {
        // Audio is enhancement only — never block the UI on failure
        console.warn('Sound playback failed:', e);
    }
};

/** playCorrect — pleasant ascending two-note chime for correct answers */
const playCorrect = () => {
    playTone(523, 0.15, 'sine', 0.3, 0);    // C5
    playTone(659, 0.2,  'sine', 0.3, 0.12); // E5
};

/** playWrong — descending square buzz for wrong answers */
const playWrong = () => {
    playTone(300, 0.1, 'square', 0.2, 0);
    playTone(200, 0.15,'square', 0.2, 0.12);
};

/** playWarningBeep — short high beep used in final 60 seconds */
const playWarningBeep = () => {
    playTone(880, 0.08, 'sine', 0.25, 0);
};

/** playComplete — ascending 4-note fanfare on quiz finish */
const playComplete = () => {
    [523, 659, 784, 1047].forEach((freq, i) => {
        playTone(freq, 0.25, 'sine', 0.35, i * 0.12);
    });
};

/**
 * playGoldenBuzzer
 * Special fanfare for a perfect 100% score.
 * Plays a rich chord followed by a rising arpeggio.
 */
const playGoldenBuzzer = () => {
    [523, 659, 784].forEach((f) => playTone(f, 0.6, 'sine', 0.25, 0));
    [523, 659, 784, 1047, 1319].forEach((f, i) =>
        playTone(f, 0.3, 'triangle', 0.3, 0.5 + i * 0.1)
    );
};

/**
 * speakText
 * Uses the browser's built-in Web Speech API to read text aloud.
 * No external library or API key required.
 * Falls back silently if the browser doesn't support it.
 *
 * Junior dev note: window.speechSynthesis is available in all
 * modern browsers. The SpeechSynthesisUtterance object holds
 * the text and speech parameters (rate, pitch, volume).
 */
const speakText = (text, rate = 0.9) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel(); // Stop any currently playing speech
    const utter  = new SpeechSynthesisUtterance(text);
    utter.rate   = rate;
    utter.pitch  = 1.1;
    utter.volume = 1;
    window.speechSynthesis.speak(utter);
};

// ============================================================
//  STYLED COMPONENTS
//  All MUI styled wrappers defined here — keeps JSX clean.
//  Junior dev note: styled() from MUI wraps any MUI or HTML
//  element with custom CSS. This avoids inline sx props for
//  styles that are reused many times.
// ============================================================

/**
 * pulseGold — keyframes animation used on the Begin button
 * and the Golden Buzzer banner.
 */
const pulseGold = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(229,201,95,0.4); }
    50%       { box-shadow: 0 0 0 20px rgba(229,201,95,0); }
`;

/**
 * shakeAnim — applied to wrong answer buttons and violation banner.
 * The horizontal shake gives clear visual feedback.
 */
const shakeAnim = keyframes`
    0%, 100% { transform: translateX(0); }
    20%       { transform: translateX(-8px); }
    40%       { transform: translateX(8px); }
    60%       { transform: translateX(-6px); }
    80%       { transform: translateX(6px); }
`;

/** PageWrapper — full-page dark background */
const PageWrapper = styled(Box)({
    minHeight:       '100vh',
    backgroundColor: BRAND.dark,
    fontFamily:      "'Poppins', sans-serif",
    padding:         '24px 16px 48px',
    overflowX:       'hidden',
});

/** QuizCard — central content card with gold border */
const QuizCard = styled(Card)({
    backgroundColor: BRAND.darkCard,
    border:          `1px solid ${BRAND.darkBorder}`,
    borderRadius:    '20px',
    backdropFilter:  'blur(10px)',
    maxWidth:        '860px',
    margin:          '0 auto',
    overflow:        'visible',
});

/** GoldButton — primary CTA with gold gradient */
const GoldButton = styled(Button)({
    background:    `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
    color:         BRAND.dark,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    800,
    fontSize:      '0.9rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding:       '12px 28px',
    borderRadius:  '10px',
    transition:    'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        background: `linear-gradient(135deg, ${BRAND.goldenrod}, ${BRAND.gold})`,
        transform:  'translateY(-2px)',
        boxShadow:  '0 8px 20px rgba(229,201,95,0.3)',
    },
    '&.Mui-disabled': {
        background: 'rgba(255,255,255,0.1)',
        color:      'rgba(255,255,255,0.3)',
    },
});

/** OutlineButton — secondary action with gold outline */
const OutlineButton = styled(Button)({
    borderColor:   BRAND.gold,
    color:         BRAND.gold,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    700,
    borderRadius:  '10px',
    padding:       '10px 24px',
    transition:    'background 0.2s',
    '&:hover': {
        backgroundColor: BRAND.goldFaint,
        borderColor:     BRAND.goldenrod,
    },
});

/**
 * ViolationBanner — red warning bar for anti-cheat alerts.
 * The shakeAnim plays on mount to draw the user's attention.
 */
const ViolationBanner = styled(Box)({
    backgroundColor: 'rgba(255,107,107,0.15)',
    border:          '1px solid rgba(255,107,107,0.4)',
    borderRadius:    '10px',
    padding:         '10px 16px',
    display:         'flex',
    alignItems:      'center',
    gap:             '10px',
    marginBottom:    '16px',
    animation:       `${shakeAnim} 0.4s ease`,
});

// ============================================================
//  STAR DISPLAY COMPONENT
//  Renders filled/outline stars based on count.
//  Used on results screen and leaderboard entries.
// ============================================================
const StarDisplay = ({ stars, size = 'large' }) => {
    const fontSize = size === 'large' ? '2.5rem' : '1.2rem';
    return (
        <Box display="flex" gap={0.5} justifyContent="center">
            {[1, 2, 3].map((i) =>
                i <= stars
                    ? <StarIcon key={i} sx={{ fontSize, color: BRAND.gold, filter: 'drop-shadow(0 0 4px rgba(229,201,95,0.6))' }} />
                    : <StarBorderIcon key={i} sx={{ fontSize, color: 'rgba(255,255,255,0.2)' }} />
            )}
        </Box>
    );
};

// ============================================================
//  MAIN COMPONENT — JavaQuiz
//
//  Add to App.js:
//    import JavaQuiz from "./pages/Languages/Java/JavaQuiz";
//    <Route path="/java-quiz" element={<JavaQuiz />} />
// ============================================================
function JavaQuiz() {

    // ── Phase & Identity ──────────────────────────────────────
    // phase controls which of the 4 screens is rendered.
    // Possible values: 'welcome' | 'rules' | 'quiz' | 'results'
    const [phase,      setPhase]     = useState('welcome');
    const [userName,   setUserName]  = useState('');
    const [nameInput,  setNameInput] = useState('');

    // ── Quiz State ────────────────────────────────────────────
    const [questions,   setQuestions]   = useState([]);
    const [current,     setCurrent]     = useState(0);
    const [score,       setScore]       = useState(0);
    const [selected,    setSelected]    = useState(null);
    const [showAnswer,  setShowAnswer]  = useState(false);
    const [userAnswers, setUserAnswers] = useState([]);
    const [isRetry,     setIsRetry]     = useState(false);

    // prevCategoryScores stores accuracy per category from the last attempt.
    // Used by reorderByDifficulty() on retry to adapt question order.
    const [prevCategoryScores, setPrevCategoryScores] = useState({});

    // ── Timer ─────────────────────────────────────────────────
    const [timer,    setTimer]    = useState(QUIZ_CONFIG.totalTimeSeconds);
    const [finished, setFinished] = useState(false);

    // ── Anti-Cheat ────────────────────────────────────────────
    const [violations,    setViolations]    = useState(0);
    const [violationLog,  setViolationLog]  = useState([]);
    const [showViolation, setShowViolation] = useState(false);
    const [lastViolation, setLastViolation] = useState('');

    // ── Refs ──────────────────────────────────────────────────
    // useRef values persist between renders but do NOT trigger re-renders.
    // violationCountRef mirrors violations state so event handler closures
    // always see the latest count (closures capture stale state otherwise).
    const violationCountRef  = useRef(0);
    const quizActiveRef      = useRef(false);  // true only during active quiz
    const warningPlayedRef   = useRef(false);  // prevents double beep

    // ============================================================
    //  QUESTION INITIALISATION
    //  First attempt: shuffle randomly.
    //  Retry: adaptive reorder — weak categories last.
    // ============================================================
    const initQuestions = useCallback((retry, catScores) => {
        let bank = javaQuestionBank.map(q => ({ ...q, options: shuffle(q.options) }));
        if (retry && Object.keys(catScores).length > 0) {
            bank = reorderByDifficulty(bank, catScores);
        } else {
            bank = shuffle(bank);
        }
        setQuestions(bank);
    }, []);

    // ============================================================
    //  ANTI-CHEAT ENGINE
    //  Attaches event listeners when phase === 'quiz'.
    //  All listeners are cleaned up when quiz ends or component unmounts.
    //
    //  Junior dev note: We attach to document/window directly because
    //  React event props only work on rendered elements, not the whole
    //  browser window. Always return a cleanup function from useEffect
    //  to remove listeners — otherwise they pile up and cause bugs.
    // ============================================================
    const recordViolation = useCallback((message) => {
        if (!quizActiveRef.current) return;

        violationCountRef.current += 1;
        const count = violationCountRef.current;

        setViolations(count);
        setViolationLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
        setLastViolation(message);
        setShowViolation(true);

        // Audio + speech feedback for violation
        playTone(440, 0.3, 'square', 0.3, 0);
        speakText('Violation detected. ' + message);

        // Auto-hide banner after 4 seconds
        setTimeout(() => setShowViolation(false), 4000);

        // Auto-terminate quiz at max violations
        if (count >= QUIZ_CONFIG.maxViolations) {
            speakText('Maximum violations reached. Quiz terminated.');
            quizActiveRef.current = false;
            setFinished(true);
        }
    }, []);

    useEffect(() => {
        if (phase !== 'quiz') return;

        // Tab switch or window minimise
        const handleVisibility = () => {
            if (document.hidden) recordViolation('Tab switch or window minimised detected.');
        };

        // Clicking outside the browser window
        const handleBlur = () => recordViolation('Browser window lost focus.');

        // Right-click disabled during quiz
        const handleContextMenu = (e) => {
            e.preventDefault();
            recordViolation('Right-click attempt blocked.');
        };

        // Block common cheat keyboard shortcuts
        const handleKeyDown = (e) => {
            const blocked =
                (e.ctrlKey && ['c', 'v', 'u', 's', 'a', 'p'].includes(e.key.toLowerCase())) ||
                e.key === 'F12' ||
                (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase()));
            if (blocked) {
                e.preventDefault();
                recordViolation(
                    `Blocked keyboard shortcut: ${e.ctrlKey ? 'Ctrl+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.key}`
                );
            }
        };

        // DevTools detection: opening DevTools changes window inner dimensions
        const handleResize = () => {
            if (
                window.outerHeight - window.innerHeight > 160 ||
                window.outerWidth  - window.innerWidth  > 160
            ) {
                recordViolation('Possible DevTools opened — browser size change detected.');
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('blur',          handleBlur);
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown',     handleKeyDown);
        window.addEventListener('resize',        handleResize);

        quizActiveRef.current = true;

        // Cleanup — runs when phase changes or component unmounts
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('blur',          handleBlur);
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown',     handleKeyDown);
            window.removeEventListener('resize',        handleResize);
            quizActiveRef.current = false;
        };
    }, [phase, recordViolation]);

    // ============================================================
    //  COUNTDOWN TIMER
    //  Decrements every second. Plays warning beep in last 60s.
    //  Auto-finishes when timer hits 0.
    // ============================================================
    useEffect(() => {
        if (phase !== 'quiz' || finished) return;

        if (timer <= 0) {
            speakText('Time is up. Quiz finished.');
            setFinished(true);
            return;
        }

        // Warning beep every 10 seconds in the last 60 seconds
        if (timer <= QUIZ_CONFIG.warningThreshold && timer % 10 === 0) {
            playWarningBeep();
            if (timer === QUIZ_CONFIG.warningThreshold) {
                speakText(`${QUIZ_CONFIG.warningThreshold} seconds remaining.`);
            }
        }

        const id = setTimeout(() => setTimer(t => t - 1), 1000);
        return () => clearTimeout(id);
    }, [timer, finished, phase]);

    // ============================================================
    //  RESULTS TRIGGER
    //  Fires when finished becomes true — saves score, plays sound.
    // ============================================================
    useEffect(() => {
        if (!finished || questions.length === 0) return;

        quizActiveRef.current = false;
        const stars = getStars(score, questions.length);

        if (stars === 3) {
            playGoldenBuzzer();
            speakText(`Congratulations ${userName}! Perfect score! You earned the Golden Buzzer and three stars!`);
        } else {
            playComplete();
            speakText(`Well done ${userName}. You scored ${score} out of ${questions.length}. You earned ${stars} star${stars !== 1 ? 's' : ''}.`);
        }

        // Build per-category stats for leaderboard entry
        const catStats = {};
        questions.forEach((q, i) => {
            if (!catStats[q.category]) catStats[q.category] = { correct: 0, total: 0 };
            catStats[q.category].total++;
            if (userAnswers[i] === q.answer) catStats[q.category].correct++;
        });
        setPrevCategoryScores(catStats);

        saveScore({
            date:          new Date().toLocaleString(),
            score,
            total:         questions.length,
            timeRemaining: timer,
            badge:         getBadge(stars),
            stars,
            name:          userName,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finished]);

    // ============================================================
    //  EVENT HANDLERS
    // ============================================================

    /**
     * handleNameSubmit — validates name and moves to rules screen.
     * speakText welcomes the user by name audibly.
     */
    const handleNameSubmit = () => {
        const trimmed = nameInput.trim();
        if (!trimmed) return;
        setUserName(trimmed);
        speakText(`Welcome ${trimmed}. Please read the rules carefully before you begin.`);
        setPhase('rules');
    };

    /**
     * handleStartQuiz — initialises all state and starts the quiz.
     * Speaks a "3... 2... 1... Begin!" countdown after a delay.
     */
    const handleStartQuiz = () => {
        initQuestions(isRetry, prevCategoryScores);
        setCurrent(0);
        setScore(0);
        setSelected(null);
        setShowAnswer(false);
        setUserAnswers([]);
        setTimer(QUIZ_CONFIG.totalTimeSeconds);
        setFinished(false);
        setViolations(0);
        setViolationLog([]);
        violationCountRef.current = 0;
        warningPlayedRef.current  = false;
        setPhase('quiz');

        // Staggered speech countdown before quiz begins
        speakText(`Are you ready, ${userName}?`, 0.85);
        setTimeout(() => speakText('3', 1.2), 1800);
        setTimeout(() => speakText('2', 1.2), 2800);
        setTimeout(() => speakText('1', 1.2), 3800);
        setTimeout(() => speakText('Begin!', 1.0), 4800);
    };

    /**
     * handleAnswer — records answer, plays sound, shows explanation.
     * showAnswer guard prevents double-answering the same question.
     */
    const handleAnswer = (opt) => {
        if (showAnswer) return;
        setSelected(opt);
        setUserAnswers(prev => [...prev, opt]);
        setShowAnswer(true);
        if (opt === questions[current].answer) {
            setScore(s => s + 1);
            playCorrect();
        } else {
            playWrong();
        }
    };

    /** handleNext — advances to next question or finishes quiz. */
    const handleNext = () => {
        if (current + 1 < questions.length) {
            setCurrent(c => c + 1);
            setShowAnswer(false);
            setSelected(null);
        } else {
            setFinished(true);
        }
    };

    /** handleRetry — switches to retry mode with adaptive ordering. */
    const handleRetry = () => {
        setIsRetry(true);
        setPhase('rules');
        speakText(`Let's try again, ${userName}. This time questions are ordered based on your previous performance.`);
    };

    // ============================================================
    //  DERIVED VALUES
    //  Computed from state — no extra useState needed.
    // ============================================================
    const categoryStats = questions.reduce((acc, q, i) => {
        if (!acc[q.category]) acc[q.category] = { correct: 0, total: 0 };
        acc[q.category].total++;
        if (finished && userAnswers[i] === q.answer) acc[q.category].correct++;
        return acc;
    }, {});

    const stars          = finished ? getStars(score, questions.length) : 0;
    const passed         = stars >= 1;
    const isGoldenBuzzer = stars === 3;
    const progressPct    = questions.length > 0 ? (current / questions.length) * 100 : 0;
    const timerWarning   = timer <= QUIZ_CONFIG.warningThreshold;

    // ============================================================
    //  RENDER — WELCOME SCREEN
    // ============================================================
    if (phase === 'welcome') {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={4}>
                            <SchoolIcon sx={{ fontSize: 56, color: BRAND.gold, mb: 1 }} />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: BRAND.white, letterSpacing: '-0.02em' }}>
                                Java Mastery Quiz
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                ProDev Adaptive Learning Platform
                            </Typography>
                            <Box mt={2}><StarDisplay stars={3} size="large" /></Box>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 4 }} />

                        <Box maxWidth={420} mx="auto">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '1rem', color: BRAND.white, mb: 0.5 }}>
                                Enter your full name to begin
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                Your name will appear on your certificate and leaderboard entry.
                            </Typography>

                            {/*
                             * We use a native <input> styled via MUI sx instead of
                             * MUI TextField because it is easier to match our dark theme.
                             * onKeyDown lets the user press Enter to submit — good UX.
                             */}
                            <Box
                                component="input"
                                placeholder="e.g. John Mensah"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
                                sx={{
                                    width: '100%', p: '14px 18px',
                                    borderRadius: '10px',
                                    border: `1px solid ${BRAND.darkBorder}`,
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    color: BRAND.white,
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '1rem', outline: 'none', mb: 3,
                                    boxSizing: 'border-box',
                                    '&:focus':       { borderColor: BRAND.gold },
                                    '&::placeholder':{ color: 'rgba(255,255,255,0.3)' },
                                }}
                            />

                            <GoldButton
                                fullWidth
                                onClick={handleNameSubmit}
                                disabled={!nameInput.trim()}
                                endIcon={<ArrowForwardIcon />}
                            >
                                Continue to Rules
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RULES SCREEN
    // ============================================================
    if (phase === 'rules') {
        const rules = [
            { icon: <TimerIcon />,               title: 'Time Limit',         text: `You have ${QUIZ_CONFIG.totalTimeSeconds / 60} minutes to answer all ${javaQuestionBank.length} questions. The timer starts immediately when you click Begin.` },
            { icon: <StarIcon />,                 title: 'Star Rewards',       text: 'Score 80%+ for ★, 90%+ for ★★, and 100% for ★★★ (Golden Buzzer!). You need at least ★ to unlock the Final Certification Exam.' },
            { icon: <ReplayIcon />,               title: 'Failed Attempts',    text: 'Scoring below 80% means you must retake. Questions will be reordered from easy to hard based on your weakest categories.' },
            { icon: <SecurityIcon />,             title: 'Anti-Cheat Rules',   text: `Do NOT switch tabs, minimise the browser, open DevTools, right-click, or use Ctrl+C/V. Each violation is logged. ${QUIZ_CONFIG.maxViolations} violations will auto-terminate your quiz.` },
            { icon: <NotificationsActiveIcon />,  title: 'Audio Enabled',      text: 'This quiz uses audio feedback and speech synthesis. Please ensure your volume is on. You will hear a countdown before the quiz starts.' },
            { icon: <SchoolIcon />,               title: 'Academic Integrity', text: 'Do not search for answers online or use external resources. This quiz tests YOUR knowledge. Plagiarism undermines your own development.' },
        ];

        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.gold }}>
                                Welcome, {userName}! 👋
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.88rem' }}>
                                {isRetry
                                    ? 'You are retaking with adaptive difficulty based on your previous performance.'
                                    : 'Please read the rules carefully before you begin.'}
                            </Typography>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 3 }} />

                        {rules.map((rule, i) => (
                            <Box key={i} display="flex" gap={2} alignItems="flex-start" mb={2}
                                 sx={{ p: 2, backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '12px' }}
                            >
                                <Box sx={{ color: BRAND.gold, mt: 0.2, flexShrink: 0 }}>{rule.icon}</Box>
                                <Box>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: BRAND.white, mb: 0.3 }}>
                                        {rule.title}
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6 }}>
                                        {rule.text}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        <Divider sx={{ borderColor: BRAND.darkBorder, my: 3 }} />

                        <Box textAlign="center">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', color: BRAND.white, mb: 3 }}>
                                Are you ready, {userName}? You will hear a countdown before the first question.
                            </Typography>
                            <GoldButton
                                onClick={handleStartQuiz}
                                startIcon={<PlayArrowIcon />}
                                sx={{ fontSize: '1rem', px: 5, py: 1.5, animation: `${pulseGold} 2s ease-in-out infinite` }}
                            >
                                I'm Ready — Begin!
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RESULTS SCREEN
    // ============================================================
    if (finished) {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>

                        {/* Golden Buzzer banner — 100% only */}
                        {isGoldenBuzzer && (
                            <Box textAlign="center" sx={{
                                background: 'linear-gradient(135deg, rgba(229,201,95,0.2), rgba(218,165,32,0.1))',
                                border: `2px solid ${BRAND.gold}`, borderRadius: '16px',
                                p: 3, mb: 3,
                                animation: `${pulseGold} 1.5s ease-in-out infinite`,
                            }}>
                                <Typography sx={{ fontSize: '2.5rem' }}>🔔</Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1.5rem', color: BRAND.gold }}>
                                    GOLDEN BUZZER!
                                </Typography>
                                <Typography sx={{ color: BRAND.dimWhite, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                    Perfect score! Extraordinary performance, {userName}!
                                </Typography>
                            </Box>
                        )}

                        {/* Score + Stars */}
                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: BRAND.white, mb: 1 }}>
                                {passed ? '🎉 Well Done,' : '😓 Keep Going,'} {userName}!
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1.1rem', color: BRAND.dimWhite, mb: 2 }}>
                                Score:{' '}
                                <Box component="span" sx={{ color: BRAND.gold, fontWeight: 700 }}>{score}</Box>
                                {' '}/ {questions.length} ({Math.round((score / questions.length) * 100)}%)
                            </Typography>
                            <StarDisplay stars={stars} size="large" />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1rem', color: BRAND.gold, mt: 1.5, fontWeight: 600 }}>
                                {getBadge(stars)}
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mt: 0.5 }}>
                                ⏱ Time remaining: {formatTime(timer)}
                            </Typography>
                        </Box>

                        {/* Certification gate — pass = unlock, fail = retry */}
                        {passed ? (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(62,207,142,0.08)', border: '1px solid rgba(62,207,142,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <EmojiEventsIcon sx={{ color: BRAND.success, fontSize: 32, mb: 0.5 }} />
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.success, fontSize: '0.95rem', mb: 2 }}>
                                    You are eligible for the Final Certification Exam!
                                </Typography>
                                <GoldButton component={Link} to="/java-certification-exam" endIcon={<ArrowForwardIcon />}>
                                    Attempt Certification Exam
                                </GoldButton>
                            </Box>
                        ) : (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.95rem', mb: 0.5 }}>
                                    You need 80%+ to unlock the Certification Exam.
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                    The retake reorders questions based on your weak areas — easier topics first.
                                </Typography>
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake Quiz (Adaptive)
                                </OutlineButton>
                            </Box>
                        )}

                        {/* Violation log — shown if any violations occurred */}
                        {violations > 0 && (
                            <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: '12px' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.88rem', mb: 1 }}>
                                    ⚠ {violations} Violation{violations > 1 ? 's' : ''} Detected
                                </Typography>
                                {violationLog.map((v, i) => (
                                    <Typography key={i} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.7)', lineHeight: 1.7 }}>
                                        {v}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        {/* Category breakdown */}
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            Category Breakdown
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {Object.entries(categoryStats).map(([cat, data]) => {
                                const pct = Math.round((data.correct / data.total) * 100);
                                const barColor = pct >= 80 ? BRAND.success : pct >= 50 ? BRAND.warning : BRAND.error;
                                return (
                                    <Box key={cat} sx={{ p: '10px 16px', backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '10px' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.82rem', color: BRAND.white }}>
                                                {cat}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: barColor, fontWeight: 700 }}>
                                                {data.correct}/{data.total} ({pct}%)
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={pct}
                                            sx={{
                                                height: 4, borderRadius: 2,
                                                backgroundColor: 'rgba(255,255,255,0.08)',
                                                '& .MuiLinearProgress-bar': { background: barColor, borderRadius: 2 },
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Leaderboard */}
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            🏅 Leaderboard — Top 5
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {loadScores().map((entry, idx) => (
                                <Box key={idx} sx={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    p: '10px 16px',
                                    backgroundColor: idx === 0 ? 'rgba(229,201,95,0.1)' : BRAND.goldFaint,
                                    border: `1px solid ${idx === 0 ? BRAND.gold : BRAND.darkBorder}`,
                                    borderRadius: '10px',
                                }}>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: idx === 0 ? BRAND.gold : BRAND.white }}>
                                            #{idx + 1} {entry.name}
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.date} • ⏱ {formatTime(entry.timeRemaining)} left
                                        </Typography>
                                    </Box>
                                    <Box textAlign="right">
                                        <StarDisplay stars={entry.stars} size="small" />
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.score}/{entry.total}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Navigation */}
                        <Box display="flex" gap={2} flexWrap="wrap">
                            <OutlineButton component={Link} to="/home" variant="outlined">
                                Back to Home
                            </OutlineButton>
                            {passed && (
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake for Better Score
                                </OutlineButton>
                            )}
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — ACTIVE QUIZ SCREEN
    // ============================================================
    if (phase === 'quiz' && questions.length > 0) {
        const q = questions[current];

        return (
            <PageWrapper>

                {/* ── Sticky progress header ──────────────────── */}
                <Box sx={{
                    position: 'sticky', top: 0, zIndex: 10,
                    backgroundColor: BRAND.dark,
                    borderBottom: `1px solid ${BRAND.darkBorder}`,
                    py: 1.5, px: 2, mb: 2,
                }}>
                    <Box maxWidth="860px" mx="auto"
                         display="flex" justifyContent="space-between"
                         alignItems="center" flexWrap="wrap" gap={1}
                    >
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.88rem', color: BRAND.dimWhite }}>
                            Question{' '}
                            <Box component="span" sx={{ color: BRAND.gold }}>{current + 1}</Box>
                            {' '}of {questions.length}
                        </Typography>

                        {/* Category label */}
                        <Chip label={q.category} size="small" sx={{
                            backgroundColor: BRAND.goldFaint, color: BRAND.gold,
                            border: `1px solid ${BRAND.darkBorder}`,
                            fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.7rem',
                        }} />

                        {/* Timer — turns red and pulses in last 60s */}
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <TimerIcon sx={{ color: timerWarning ? BRAND.error : BRAND.gold, fontSize: '1.1rem' }} />
                            <Typography sx={{
                                fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1rem',
                                color: timerWarning ? BRAND.error : BRAND.gold,
                                animation: timerWarning ? `${pulseGold} 1s ease-in-out infinite` : 'none',
                            }}>
                                {formatTime(timer)}
                            </Typography>
                        </Box>

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.dimWhite }}>
                            Score: <Box component="span" sx={{ color: BRAND.gold }}>{score}</Box>
                        </Typography>
                    </Box>

                    {/* Gold progress bar */}
                    <Box maxWidth="860px" mx="auto" mt={1}>
                        <LinearProgress
                            variant="determinate"
                            value={progressPct}
                            sx={{
                                height: 4, borderRadius: 2,
                                backgroundColor: 'rgba(255,255,255,0.06)',
                                '& .MuiLinearProgress-bar': {
                                    background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* ── Anti-cheat violation banner ─────────────── */}
                {showViolation && (
                    <Box maxWidth="860px" mx="auto" px={2} mb={1}>
                        <ViolationBanner>
                            <WarningAmberIcon sx={{ color: BRAND.error, flexShrink: 0 }} />
                            <Box>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.error }}>
                                    ⚠ Violation #{violations} Detected
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.8)' }}>
                                    {lastViolation} — {QUIZ_CONFIG.maxViolations - violations} more will terminate this quiz.
                                </Typography>
                            </Box>
                        </ViolationBanner>
                    </Box>
                )}

                {/* ── Question card ───────────────────────────── */}
                <QuizCard>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>

                        {/* Question text */}
                        <Typography sx={{
                            fontFamily: "'Poppins', sans-serif", fontWeight: 700,
                            fontSize: { xs: '1rem', md: '1.15rem' },
                            color: BRAND.white, mb: 3, lineHeight: 1.6,
                            p: 2.5,
                            backgroundColor: BRAND.goldFaint,
                            borderLeft: `3px solid ${BRAND.gold}`,
                            borderRadius: '0 10px 10px 0',
                        }}>
                            {current + 1}. {q.question}
                        </Typography>

                        {/* Answer options */}
                        <Box>
                            {q.options.map((opt, i) => {
                                // Determine visual state of each option after answering
                                // correct  = always highlight the right answer green
                                // wrong    = highlight user's wrong pick in red
                                // default  = unselected, no answer yet
                                const isCorrect  = showAnswer && opt === q.answer;
                                const isWrong    = showAnswer && opt === selected && opt !== q.answer;
                                const bgColor    = isCorrect ? 'rgba(62,207,142,0.15)'  : isWrong ? 'rgba(255,107,107,0.15)' : 'rgba(255,255,255,0.04)';
                                const borderClr  = isCorrect ? BRAND.success            : isWrong ? BRAND.error              : 'rgba(255,255,255,0.12)';
                                const textColor  = isCorrect ? BRAND.success            : isWrong ? BRAND.error              : BRAND.white;

                                return (
                                    <Button
                                        key={i}
                                        onClick={() => handleAnswer(opt)}
                                        disabled={showAnswer}
                                        startIcon={
                                            isCorrect ? <CheckCircleIcon sx={{ color: BRAND.success }} /> :
                                                isWrong   ? <CancelIcon      sx={{ color: BRAND.error }}   /> :
                                                    null
                                        }
                                        sx={{
                                            width: '100%', textAlign: 'left',
                                            justifyContent: 'flex-start',
                                            p: '14px 20px', borderRadius: '10px',
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: 500, fontSize: '0.9rem',
                                            textTransform: 'none', mb: 1,
                                            backgroundColor: bgColor,
                                            border: `1px solid ${borderClr}`,
                                            color: textColor,
                                            transition: 'all 0.25s ease',
                                            animation: isWrong ? `${shakeAnim} 0.4s ease` : 'none',
                                            '&:hover': !showAnswer ? {
                                                backgroundColor: 'rgba(229,201,95,0.08)',
                                                borderColor: BRAND.gold,
                                            } : {},
                                            '&.Mui-disabled': { opacity: 1 }, // Keep colour when disabled
                                        }}
                                    >
                                        {opt}
                                    </Button>
                                );
                            })}
                        </Box>

                        {/* Explanation box — appears after answering */}
                        {showAnswer && (
                            <Box sx={{
                                mt: 2, p: 2,
                                backgroundColor: selected === q.answer
                                    ? 'rgba(62,207,142,0.06)' : 'rgba(255,107,107,0.06)',
                                border: `1px solid ${selected === q.answer
                                    ? 'rgba(62,207,142,0.3)' : 'rgba(255,107,107,0.3)'}`,
                                borderRadius: '10px',
                            }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.85rem', color: selected === q.answer ? BRAND.success : BRAND.error, mb: 0.5 }}>
                                    {selected === q.answer ? '✅ Correct!' : `❌ Incorrect — Correct answer: ${q.answer}`}
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6, fontStyle: 'italic' }}>
                                    {q.explanation}
                                </Typography>
                                {/* Link to the relevant lesson */}
                                <Typography
                                    component={Link}
                                    to={q.link}
                                    sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: BRAND.gold, mt: 0.5, display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    📖 Review this topic →
                                </Typography>
                            </Box>
                        )}

                        {/* Next / Finish button */}
                        {showAnswer && (
                            <Box mt={3} display="flex" justifyContent="flex-end">
                                <GoldButton onClick={handleNext} endIcon={<ArrowForwardIcon />}>
                                    {current + 1 < questions.length ? 'Next Question' : 'Finish Quiz'}
                                </GoldButton>
                            </Box>
                        )}
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // Fallback — should never appear in normal flow
    return (
        <PageWrapper>
            <Typography sx={{ color: BRAND.white, textAlign: 'center', pt: 10, fontFamily: "'Poppins', sans-serif" }}>
                Loading quiz...
            </Typography>
        </PageWrapper>
    );
}

export default JavaQuiz;
